import { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Input,
  Button,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  useColorModeValue,
  useToast,
  Flex,
  Text,
  Link as ChakraLink,
  Spinner,
  Image,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../context/useAuth";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";

export default function Login() {
  const { signIn, user, loading, error } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const bgColor = useColorModeValue("gray.100", "gray.900");
  const cardColor = useColorModeValue("white", "gray.800");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const handleLogin = async () => {
    const result = await signIn({ email, password });

    if (result?.message) {
      setErrorMsg(result.message);
      toast({
        title: "Login failed",
        description: result.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Login successful",
        description: "Redirecting to dashboard...",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate("/dashboard", { replace: true });
    }
  };

  const handleGoogleLogin = async () => {
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: "https://www.postcode.coupons/"
      },
    });

    if (oauthError) {
      toast({
        title: "Google login failed",
        description: oauthError.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Login | postcode.coupons</title>
      </Helmet>

      <Flex minH="100vh" align="center" justify="center" bg={bgColor}>
        <Box bg={cardColor} p={8} rounded="md" shadow="lg" width="100%" maxW="md">
          
          <Flex justify="center" mb={4}>
            <Image src="https://www.postcode.coupons/logo512.png" alt="Postcode Coupons" boxSize="64px" />
          </Flex>

          <Heading mb={6} textAlign="center" color="red.600">
            🔐 Login
          </Heading>

          {loading && (
            <Flex justify="center" mb={6}>
              <Spinner size="lg" color="red.500" />
            </Flex>
          )}

          {(errorMsg || error) && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              {errorMsg || error?.message}
            </Alert>
          )}

          <FormControl mb={4} isRequired>
            <FormLabel>Email</FormLabel>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="you@example.com" />
          </FormControl>

          <FormControl mb={6} isRequired>
            <FormLabel>Password</FormLabel>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" placeholder="••••••••" />
          </FormControl>

          <Button colorScheme="red" onClick={handleLogin} w="full" mb={4} isDisabled={!email || !password || loading}>
            Login with Password
          </Button>

          <Button
            bg="white"
            color="black"
            border="1px solid #ddd"
            _hover={{ bg: "gray.100" }}
            w="full"
            mb={4}
            isDisabled={loading}
            onClick={handleGoogleLogin}
          >
            <Image src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" boxSize="20px" mr={2} />
            Sign in with Google
          </Button>

          <Text textAlign="center">
            Don’t have an account?{" "}
            <ChakraLink as={Link} to="/signup" color="red.500">
              Sign up
            </ChakraLink>
          </Text>
        </Box>
      </Flex>
    </>
  );
}
